import { EntityType } from '../model/common/constant'
import type { ILine, IPoint } from '../model/common/interface'
import type { IEntity, PolylineEntity } from '../model/entity'

const EPSILON = 1e-6
const ARC_SEGMENTS_NUM = 32

/**
 * 角度标注候选线段：支持直线段与由 bulge 生成的弧段两类。
 */
export interface AngularDimensionSegmentCandidate {
  /** 候选段唯一标识 */
  id: string
  /** 候选段类型：直线或弧 */
  kind: 'line' | 'arc'
  /** 用于角度计算的参考直线（弧段为其弦） */
  line: ILine
  /** 用于命中/预览的离散直线集合 */
  previewLines: ILine[]
  /** 起点本地坐标 */
  startPoint: IPoint
  /** 终点本地坐标 */
  endPoint: IPoint
  /** 起点处切线，仅弧段具备 */
  tangentAtStart?: ILine
  /** 终点处切线，仅弧段具备 */
  tangentAtEnd?: ILine
}

interface BulgeArcParams {
  center: IPoint
  radius: number
  startAngle: number
  sweepAngle: number
}

/**
 * 根据实体构建角度标注可选段集合，供两段拾取与角度计算使用。
 *
 * @param entity - 参与角度标注的源实体
 * @returns 候选段集合，多段线会按 bulge 拆分为直线或弧
 */
export function buildAngularDimensionSegmentCandidates(
  entity: IEntity
): AngularDimensionSegmentCandidate[] {
  if (entity.type === EntityType.Polyline) {
    return buildPolylineSegmentCandidates(entity as PolylineEntity)
  }

  const lines =
    (entity as unknown as { getCaptureData: () => { lines?: ILine[] } }).getCaptureData().lines ||
    []
  return lines.map((line, index) => createLineCandidate(`${entity.id}:line:${index}`, line))
}

/**
 * 计算点到候选段的最小距离。
 *
 * @param point - 待测点
 * @param candidate - 候选段
 * @returns 点到候选段的最小距离
 */
export function getAngularDimensionSegmentDistance(
  point: IPoint,
  candidate: AngularDimensionSegmentCandidate
) {
  let minDistance = Number.POSITIVE_INFINITY

  for (const line of candidate.previewLines) {
    const nearestPoint = projectPointOnLineSegment(point, line)
    minDistance = Math.min(minDistance, getDistance(nearestPoint, point))
  }

  return minDistance
}

/**
 * 解析两个候选段之间用于角度计算的参考直线对，弧段优先取共享端点的切线。
 *
 * @param first - 第一个候选段
 * @param second - 第二个候选段
 * @returns 两段各自用于角度计算的直线
 */
export function resolveAngularDimensionLinePair(
  first: AngularDimensionSegmentCandidate,
  second: AngularDimensionSegmentCandidate
) {
  return {
    line1: resolveAngularDimensionLine(first, second),
    line2: resolveAngularDimensionLine(second, first),
  }
}

function buildPolylineSegmentCandidates(entity: PolylineEntity) {
  const candidates: AngularDimensionSegmentCandidate[] = []
  const vertices = entity.vertices || []
  if (vertices.length < 2) {
    return candidates
  }

  const segmentCount = entity.isClosed ? vertices.length : vertices.length - 1
  for (let index = 0; index < segmentCount; index++) {
    const start = vertices[index]
    const end = vertices[(index + 1) % vertices.length]
    const line = {
      startPoint: { x: start.x, y: start.y },
      endPoint: { x: end.x, y: end.y },
    }
    const bulge = start.bulge || 0

    if (Math.abs(bulge) < EPSILON) {
      candidates.push(createLineCandidate(`${entity.id}:polyline:${index}:line`, line))
      continue
    }

    const arcParams = getBulgeArcParams(line.startPoint, line.endPoint, bulge)
    if (!arcParams) {
      candidates.push(createLineCandidate(`${entity.id}:polyline:${index}:line`, line))
      continue
    }

    const previewPoints = sampleBulgeArcPoints(
      line.startPoint,
      line.endPoint,
      bulge,
      ARC_SEGMENTS_NUM
    )
    const previewLines = getLinesByPoints(previewPoints)
    const tangentLength = Math.max(getDistance(line.startPoint, line.endPoint), arcParams.radius, 1)

    candidates.push({
      id: `${entity.id}:polyline:${index}:arc`,
      kind: 'arc',
      line,
      previewLines,
      startPoint: clonePoint(line.startPoint),
      endPoint: clonePoint(line.endPoint),
      tangentAtStart: createTangentLine(
        line.startPoint,
        arcParams.startAngle,
        arcParams.sweepAngle,
        tangentLength
      ),
      tangentAtEnd: createTangentLine(
        line.endPoint,
        arcParams.startAngle + arcParams.sweepAngle,
        arcParams.sweepAngle,
        tangentLength
      ),
    })
  }

  return candidates
}

function resolveAngularDimensionLine(
  current: AngularDimensionSegmentCandidate,
  other: AngularDimensionSegmentCandidate
) {
  if (current.kind === 'line') {
    return cloneLine(current.line)
  }

  if (
    isSamePoint(current.startPoint, other.startPoint) ||
    isSamePoint(current.startPoint, other.endPoint)
  ) {
    return cloneLine(current.tangentAtStart || current.line)
  }
  if (
    isSamePoint(current.endPoint, other.startPoint) ||
    isSamePoint(current.endPoint, other.endPoint)
  ) {
    return cloneLine(current.tangentAtEnd || current.line)
  }

  return cloneLine(current.line)
}

function createLineCandidate(id: string, line: ILine): AngularDimensionSegmentCandidate {
  const clonedLine = cloneLine(line)
  return {
    id,
    kind: 'line',
    line: clonedLine,
    previewLines: [cloneLine(clonedLine)],
    startPoint: clonePoint(clonedLine.startPoint),
    endPoint: clonePoint(clonedLine.endPoint),
  }
}

function sampleBulgeArcPoints(
  startPoint: IPoint,
  endPoint: IPoint,
  bulge: number,
  segmentsNum: number
) {
  const arcParams = getBulgeArcParams(startPoint, endPoint, bulge)
  if (!arcParams) {
    return [clonePoint(startPoint), clonePoint(endPoint)]
  }

  const points: IPoint[] = []
  for (let index = 0; index <= segmentsNum; index++) {
    const t = index / segmentsNum
    const angle = arcParams.startAngle + arcParams.sweepAngle * t
    points.push({
      x: arcParams.center.x + Math.cos(angle) * arcParams.radius,
      y: arcParams.center.y + Math.sin(angle) * arcParams.radius,
    })
  }

  points[0] = clonePoint(startPoint)
  points[points.length - 1] = clonePoint(endPoint)
  return points
}

function getBulgeArcParams(
  startPoint: IPoint,
  endPoint: IPoint,
  bulge: number
): BulgeArcParams | null {
  if (Math.abs(bulge) < EPSILON) {
    return null
  }

  const dx = endPoint.x - startPoint.x
  const dy = endPoint.y - startPoint.y
  const chordLength = Math.sqrt(dx * dx + dy * dy)
  if (chordLength < EPSILON) {
    return null
  }

  const midPoint = {
    x: (startPoint.x + endPoint.x) / 2,
    y: (startPoint.y + endPoint.y) / 2,
  }
  const normal = {
    x: -dy / chordLength,
    y: dx / chordLength,
  }
  const offset = ((chordLength / 2) * (1 - bulge * bulge)) / (2 * bulge)
  const center = {
    x: midPoint.x + normal.x * offset,
    y: midPoint.y + normal.y * offset,
  }

  return {
    center,
    radius: Math.sqrt((chordLength / 2) ** 2 + offset ** 2),
    startAngle: Math.atan2(startPoint.y - center.y, startPoint.x - center.x),
    sweepAngle: 4 * Math.atan(bulge),
  }
}

function createTangentLine(
  point: IPoint,
  angle: number,
  sweepAngle: number,
  length: number
): ILine {
  const direction =
    sweepAngle >= 0
      ? { x: -Math.sin(angle), y: Math.cos(angle) }
      : { x: Math.sin(angle), y: -Math.cos(angle) }

  return {
    startPoint: clonePoint(point),
    endPoint: {
      x: point.x + direction.x * length,
      y: point.y + direction.y * length,
    },
  }
}

function getLinesByPoints(points: IPoint[]) {
  const lines: ILine[] = []
  for (let index = 0; index < points.length - 1; index++) {
    lines.push({
      startPoint: clonePoint(points[index]),
      endPoint: clonePoint(points[index + 1]),
    })
  }
  return lines
}

function projectPointOnLineSegment(point: IPoint, line: ILine) {
  const dx = line.endPoint.x - line.startPoint.x
  const dy = line.endPoint.y - line.startPoint.y
  const lengthSquared = dx * dx + dy * dy

  if (lengthSquared < EPSILON) {
    return clonePoint(line.startPoint)
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - line.startPoint.x) * dx + (point.y - line.startPoint.y) * dy) / lengthSquared
    )
  )

  return {
    x: line.startPoint.x + t * dx,
    y: line.startPoint.y + t * dy,
  }
}

function getDistance(point1: IPoint, point2: IPoint) {
  const dx = point1.x - point2.x
  const dy = point1.y - point2.y
  return Math.sqrt(dx * dx + dy * dy)
}

function isSamePoint(point1: IPoint, point2: IPoint, tolerance: number = EPSILON) {
  return Math.abs(point1.x - point2.x) <= tolerance && Math.abs(point1.y - point2.y) <= tolerance
}

function cloneLine(line: ILine): ILine {
  return {
    startPoint: clonePoint(line.startPoint),
    endPoint: clonePoint(line.endPoint),
  }
}

function clonePoint(point: IPoint): IPoint {
  return {
    x: point.x,
    y: point.y,
  }
}
